import React, { useState, useEffect, useRef } from "react";
import { calculateRankings, distances, raceTabs } from "../utils/calculatePoints";
import { Box, FormControl, InputLabel, Select, MenuItem, TextField, Table, TableBody, TableCell, TableHead, TableRow, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { collection, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const Rankings: React.FC = () => {
  const [rankings, setRankings] = useState<{ [key: string]: any }>({});
  const [search, setSearch] = useState("");
  const [selectedDistance, setSelectedDistance] = useState("");
  const [selectedBracket, setSelectedBracket] = useState("");
  const [title, setTitle] = useState("San Diego Triathlon Series Rankings");
  const bracketRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<any>(null);
  const [openPopup, setOpenPopup] = useState(false);

  // Initial fetch for rankings and title
  useEffect(() => {
    calculateRankings().then(setRankings);
    const fetchTitle = async () => {
      try {
        const titleDoc = await getDoc(doc(db, "settings", "siteConfig"));
        if (titleDoc.exists()) {
          setTitle(titleDoc.data().title || "San Diego Triathlon Series Rankings");
        }
      } catch (error) {
        console.error("Error fetching title:", error);
      }
    };
    fetchTitle();
  }, []);

  // Real-time listeners for race collections and title
  useEffect(() => {
    const unsubscribes = raceTabs.map((tab) =>
      onSnapshot(collection(db, tab), () => {
        console.log(`Data change detected in ${tab} - recalculating rankings...`);
        calculateRankings().then(setRankings);
      })
    );
    const titleUnsubscribe = onSnapshot(doc(db, "settings", "siteConfig"), (doc) => {
      if (doc.exists()) {
        setTitle(doc.data().title || "San Diego Triathlon Series Rankings");
      }
    });

    // Back to top visibility on scroll
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);

    // Cleanup
    return () => {
      unsubscribes.forEach((unsub) => unsub());
      titleUnsubscribe();
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Update selected bracket when distance changes
  useEffect(() => {
    if (selectedDistance) {
      const brackets = Object.keys(rankings[selectedDistance] || {});
      setSelectedBracket("");
    } else {
      setSelectedBracket("");
    }
  }, [selectedDistance, rankings]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openDetailsPopup = (participant: any) => {
    setSelectedParticipant(participant);
    setOpenPopup(true);
  };

  const closeDetailsPopup = () => {
    setOpenPopup(false);
    setSelectedParticipant(null);
  };

  // Helper to get ordinal suffix for rank (e.g., 1st, 2nd, 3rd)
  const getOrdinalSuffix = (rank: number): string => {
    const j = rank % 10,
          k = rank % 100;
    if (j === 1 && k !== 11) return `${rank}st`;
    if (j === 2 && k !== 12) return `${rank}nd`;
    if (j === 3 && k !== 13) return `${rank}rd`;
    return `${rank}th`;
  };

  const currentDistance = selectedDistance;
  const bracketList = Object.keys(rankings[currentDistance] || {});
  // Calculate active events (those with non-zero points)
  const activeEvents = raceTabs.filter((event) =>
    bracketList.some((bracket) =>
      (rankings[currentDistance][bracket] || []).some((p: any) => (p.events[event] || 0) > 0)
    )
  );

  // Global search results (across all distances and brackets, excluding 0 events)
  const globalSearchResults = Object.entries(rankings).flatMap(([distance, brackets]: any) =>
    Object.entries(brackets).flatMap(([bracket, participants]: any) =>
      participants
        .filter((p: any) => p.eventCount > 0 && p.name.toLowerCase().includes(search.toLowerCase()))
        .map((p: any) => ({ ...p, distance, bracket }))
    )
  );

  const showResults = selectedDistance && selectedBracket && !search;

  return (
    <Box sx={{ p: 2, maxWidth: "100%", overflowX: "auto" }}>
      <img src={`${process.env.PUBLIC_URL}/GTRR.png`} alt="GTRR Logo" style={{ maxWidth: "200px", marginBottom: "16px" }} />
      <Typography variant="h4" sx={{ mb: 2, textAlign: "center" }}>{title}</Typography>

      {/* Race (Distance) Select */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Select Race</InputLabel>
        <Select value={selectedDistance} onChange={(e) => setSelectedDistance(e.target.value)}>
          <MenuItem value="" disabled>
            Select a Race
          </MenuItem>
          {distances.map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Division (Bracket) Select */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Select Division</InputLabel>
        <Select value={selectedBracket} onChange={(e) => setSelectedBracket(e.target.value)} disabled={!selectedDistance}>
          <MenuItem value="" disabled>
            Select a Division
          </MenuItem>
          <MenuItem value="View All">View All</MenuItem>
          {bracketList.map((bracket) => (
            <MenuItem key={bracket} value={bracket}>
              {bracket}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Search Name"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        placeholder="Enter name (partial match)"
      />
      <Button onClick={() => setSearch("")} sx={{ mb: 2, ml: 2 }}>Clear Search</Button>
      <Button onClick={() => calculateRankings().then(setRankings)} sx={{ mb: 2, ml: 2 }}>Refresh Rankings</Button>

      {/* Global Search Results */}
      {search && globalSearchResults.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            Search Results
          </Typography>
          {globalSearchResults.map((p: any, idx: number) => (
            <Box key={idx} sx={{ mb: 2, p: 2, border: "1px solid #ddd", borderRadius: 1 }}>
              <Typography variant="subtitle1">{p.name} - {p.distance} - {p.bracket}</Typography>
              <Typography>Rank: {p.rank}</Typography>
              <Typography>Total Points: {p.totalPoints}</Typography>
              <Typography>Events: {p.eventCount}</Typography>
              <Button variant="outlined" size="small" onClick={() => openDetailsPopup(p)} sx={{ mt: 1 }}>
                View Details
              </Button>
            </Box>
          ))}
        </Box>
      ) : search && globalSearchResults.length === 0 ? (
        <Typography sx={{ mt: 2 }}>No participants found.</Typography>
      ) : null}

      {/* Selected Bracket Table or All Brackets for View All */}
      {showResults && (rankings[selectedDistance]?.[selectedBracket] || selectedBracket === "View All") ? (
        <Box sx={{ mt: 2 }}>
          {selectedBracket === "View All" ? (
            Object.keys(rankings[selectedDistance] || {}).map((bracket) => (
              <Box
                key={bracket}
                sx={{ mt: 2 }}
                ref={(el: HTMLDivElement | null) => {
                  bracketRefs.current[bracket] = el;
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  {bracket}
                </Typography>
                <Table size="small" sx={{ tableLayout: "auto" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: { xs: "10%", sm: "5%" }, fontWeight: "bold" }}>Rank</TableCell>
                      <TableCell sx={{ width: { xs: "50%", sm: "40%" }, fontWeight: "bold" }}>Name</TableCell>
                      <TableCell sx={{ width: { xs: "20%", sm: "25%" }, fontWeight: "bold" }}>Total Points</TableCell>
                      <TableCell sx={{ width: { xs: "20%", sm: "25%" }, fontWeight: "bold" }}>Events</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(rankings[selectedDistance][bracket] || [])
                      .filter((p: any) => p.eventCount > 0)
                      .map((p: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>{p.rank}</TableCell>
                          <TableCell sx={{ cursor: "pointer", color: "primary.main", "&:hover": { textDecoration: "underline" } }} onClick={() => openDetailsPopup(p)}>
                            {p.name}
                          </TableCell>
                          <TableCell>{p.totalPoints}</TableCell>
                          <TableCell>{p.eventCount}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>
            ))
          ) : (
            <Box
              sx={{ mt: 2 }}
              ref={(el: HTMLDivElement | null) => {
                bracketRefs.current[selectedBracket] = el;
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                {selectedBracket}
              </Typography>
              <Table size="small" sx={{ tableLayout: "auto" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: { xs: "10%", sm: "5%" }, fontWeight: "bold" }}>Rank</TableCell>
                    <TableCell sx={{ width: { xs: "50%", sm: "40%" }, fontWeight: "bold" }}>Name</TableCell>
                    <TableCell sx={{ width: { xs: "20%", sm: "25%" }, fontWeight: "bold" }}>Total Points</TableCell>
                    <TableCell sx={{ width: { xs: "20%", sm: "25%" }, fontWeight: "bold" }}>Events</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rankings[selectedDistance][selectedBracket] || [])
                    .filter((p: any) => p.eventCount > 0)
                    .map((p: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell>{p.rank}</TableCell>
                        <TableCell sx={{ cursor: "pointer", color: "primary.main", "&:hover": { textDecoration: "underline" } }} onClick={() => openDetailsPopup(p)}>
                          {p.name}
                        </TableCell>
                        <TableCell>{p.totalPoints}</TableCell>
                        <TableCell>{p.eventCount}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      ) : !search && (
        <Typography sx={{ mt: 2 }}>Please select a Race and Division to view rankings.</Typography>
      )}

      {/* Participant Details Popup */}
      <Dialog
        open={openPopup}
        onClose={closeDetailsPopup}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            bgcolor: "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "white",
            fontWeight: "bold",
            py: 2,
            textAlign: "center",
          }}
        >
          {selectedParticipant ? `${selectedParticipant.name} - ${selectedParticipant.totalPoints} Points - ${getOrdinalSuffix(selectedParticipant.rank)} Place` : ""}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedParticipant && (
            <Typography sx={{ mb: 2, color: "text.secondary", textAlign: "center" }}>
              {selectedParticipant.city}, {selectedParticipant.regionCode}
            </Typography>
          )}
          {selectedParticipant?.eventDetails.length > 0 ? (
            <Table
              size="small"
              sx={{
                "& .MuiTableCell-root": {
                  borderBottom: "1px solid #e0e0e0",
                  py: 1.5,
                },
                "& .MuiTableRow-root:nth-of-type(even)": {
                  bgcolor: "#f9f9f9",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>Event</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>Points</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedParticipant.eventDetails.map((detail: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ color: "text.secondary" }}>{detail.event.replace(/([A-Z])/g, " $1").trim()}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{detail.time || "N/A"}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{detail.rank}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{detail.points}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography sx={{ color: "text.secondary", py: 2 }}>No event details available.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={closeDetailsPopup}
            variant="contained"
            sx={{
              bgcolor: "primary.main",
              "&:hover": { bgcolor: "primary.dark" },
              px: 3,
              py: 1,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {showBackToTop && (
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            bgcolor: "primary.main",
            color: "white",
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          <ArrowUpwardIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default Rankings;